import { Form, Select, Button, Input } from "antd";
import { useTranslation } from "react-i18next";
import { MachineItem, ProductCardItem } from "shared/api";
import { FileAttachmentsList } from "./FileAttachmentsList";

interface AssociationsSectionProps {
  machinesData: MachineItem[] | undefined;
  productCardsData: ProductCardItem[] | undefined;
  suppliersCompaniesFormatted:
    | {
        label: string;
        value: number;
      }[]
    | undefined;
  showDescription: boolean;
  attachedFiles: any[];
  newFiles: File[];
  themeColor: string;
  fileDocRef: React.RefObject<HTMLInputElement>;
  onToggleDescription: () => void;
  onAttachFiles: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDeleteFile: (fileId: number) => void;
  onRemoveNewFile: (index: number) => void;
  showEntityAssociations?: boolean;
  associationsIntroText?: string;
}

export const AssociationsSection: React.FC<AssociationsSectionProps> = ({
  machinesData,
  productCardsData,
  suppliersCompaniesFormatted,
  showDescription,
  attachedFiles,
  newFiles,
  themeColor,
  fileDocRef,
  onToggleDescription,
  onAttachFiles,
  onDeleteFile,
  onRemoveNewFile,
  showEntityAssociations = true,
  associationsIntroText,
}) => {
  const { t } = useTranslation();

  return (
    <div>
      {showEntityAssociations && (
        <>
          <h3 className="text-base font-semibold mb-2">
            {t("taskHub.modal.associations") || ""}
          </h3>
          {associationsIntroText ? (
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-3">
              {associationsIntroText}
            </p>
          ) : null}
          <div className="grid grid-cols-1 gap-x-4">
            <div className="w-full flex flex-col gap-4">
              <div className="flex w-full gap-4">
                <Form.Item
                  name="machines"
                  label={t("taskHub.modal.machines") || ""}
                  className="flex-1 !mb-0"
                >
                  <Select
                    placeholder={t("taskHub.modal.selectMachines") || ""}
                    mode="multiple"
                    options={
                      machinesData?.map((m) => ({
                        label: m.name,
                        value: m.id,
                      })) || []
                    }
                  />
                </Form.Item>

                <Form.Item
                  name="product_cards"
                  label={t("taskHub.modal.productCards") || ""}
                  className="flex-1 truncate overflow-x-hidden !mb-0"
                >
                  <Select
                    placeholder={t("taskHub.modal.selectProductCards") || ""}
                    showSearch
                    optionFilterProp="label"
                    mode="multiple"
                    options={
                      productCardsData?.map((p) => ({
                        label: p.name,
                        value: p.id,
                      })) || []
                    }
                  />
                </Form.Item>
              </div>

              <div className="grid grid-cols-2 gap-x-4">
                <Form.Item
                  name="companies"
                  label={t("taskHub.modal.suppliers") || ""}
                >
                  <Select
                    mode="multiple"
                    showSearch
                    options={suppliersCompaniesFormatted}
                    optionFilterProp="label"
                    placeholder={t("taskHub.modal.selectSupplier") || ""}
                    filterOption={(input, option) =>
                      (option?.label as string)
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="flex gap-2 flex-col">
        <div className="flex gap-x-2">
          <Form.Item className="!mb-0">
            <Button
              type="primary"
              style={{ backgroundColor: themeColor }}
              className="!mb-0"
              onClick={onToggleDescription}
            >
              {showDescription
                ? t("taskHub.modal.clearDescription") || ""
                : t("taskHub.modal.addDescription") || ""}
            </Button>
          </Form.Item>

          <Form.Item className="!mb-0">
            <div className="flex items-center gap-3">
              <input
                type="file"
                multiple
                onChange={onAttachFiles}
                ref={fileDocRef}
                className="!hidden"
              />
              <Button
                type="primary"
                style={{ backgroundColor: themeColor }}
                className="!mb-0"
                onClick={() => fileDocRef.current?.click()}
              >
                {t("taskHub.modal.attachFiles") || ""}
              </Button>
            </div>
          </Form.Item>
        </div>

        <div>
          {showDescription && (
            <Form.Item
              name="description"
              label={t("taskHub.modal.description") || ""}
              className="col-span-2"
            >
              <Input.TextArea rows={4} />
            </Form.Item>
          )}

          <FileAttachmentsList
            attachedFiles={attachedFiles}
            newFiles={newFiles}
            themeColor={themeColor}
            onDeleteFile={onDeleteFile}
            onRemoveNewFile={onRemoveNewFile}
          />
        </div>
      </div>
    </div>
  );
};
